import * as Rematrix from "rematrix";
import assign from "object-assign";
import springUpdate from "./spring";
import { getSpringConfig } from "../../springSettings";
import { toArray, isFunction, isNumber, getDuplicateValsAsStrings } from "../utilities";
import * as constants from "../../constants"; // 3d transforms were causing weird issues in chrome,
// especially when opacity was also being tweened,
// so convert to a 2d matrix

export var convertMatrix3dArrayTo2dArray = function convertMatrix3dArrayTo2dArray(matrix) {
  return [// scale X
  matrix[0], matrix[1], matrix[4], // scale Y
  matrix[5], // translation X
  matrix[12], // translation Y
  matrix[13]];
};
export var convertMatrix2dArrayToString = function convertMatrix2dArrayToString(matrix) {
  return "matrix(" + matrix.join(", ") + ")";
};
export var invertTransformsForChildren = function invertTransformsForChildren(_ref) {
  var invertedChildren = _ref.invertedChildren,
      matrix = _ref.matrix,
      body = _ref.body;
  invertedChildren.forEach(function (_ref2) {
    var child = _ref2[0],
        childFlipConfig = _ref2[1];

    if (!body.contains(child)) {
      return;
    }

    var scaleX = matrix[0];
    var scaleY = matrix[3];
    var translateX = matrix[4];
    var translateY = matrix[5];
    var inverseVals = {
      translateX: 0,
      translateY: 0,
      scaleX: 1,
      scaleY: 1
    };
    var transformString = "";

    if (childFlipConfig.translate) {
      inverseVals.translateX = -translateX / scaleX;
      inverseVals.translateY = -translateY / scaleY;
      transformString += "translate(" + inverseVals.translateX + "px, " + inverseVals.translateY + "px)";
    }

    if (childFlipConfig.scale) {
      inverseVals.scaleX = 1 / scaleX;
      inverseVals.scaleY = 1 / scaleY;
      transformString += " scale(" + inverseVals.scaleX + ", " + inverseVals.scaleY + ")";
    }

    child.style.transform = transformString;
  });
};
export var createApplyStylesFunc = function createApplyStylesFunc(_ref3) {
  var element = _ref3.element,
      invertedChildren = _ref3.invertedChildren,
      body = _ref3.body;
  return function (_ref4) {
    var matrix = _ref4.matrix,
        opacity = _ref4.opacity,
        forceMinVals = _ref4.forceMinVals;

    if (isNumber(opacity)) {
      element.style.opacity = opacity;
    }

    if (forceMinVals) {
      element.style.minHeight = "1px";
      element.style.minWidth = "1px";
    }

    if (!matrix) return;
    element.style.transform = convertMatrix2dArrayToString(matrix);

    if (invertedChildren) {
      invertTransformsForChildren({
        invertedChildren: invertedChildren,
        matrix: matrix,
        body: body
      });
    }
  };
};
export var rectInViewport = function rectInViewport(_ref5) {
  var top = _ref5.top,
      bottom = _ref5.bottom,
      left = _ref5.left,
      right = _ref5.right;
  return top < window.innerHeight && bottom > 0 && left < window.innerWidth && right > 0;
};

var passesComponentFilter = function passesComponentFilter(flipComponentIdFilter, flipId) {
  if (typeof flipComponentIdFilter === "string") {
    if (flipComponentIdFilter !== flipId) return false;
  } else if (Array.isArray(flipComponentIdFilter)) {
    if (!flipComponentIdFilter.some(function (f) {
      return f === flipId;
    })) {
      return false;
    }
  }

  return true;
};

export var shouldApplyTransform = function shouldApplyTransform(flipComponentIdFilter, flipStartId, flipEndId) {
  if (flipComponentIdFilter && !passesComponentFilter(flipComponentIdFilter, flipStartId) && !passesComponentFilter(flipComponentIdFilter, flipEndId)) {
    return false;
  }

  return true;
};

var getInvertedChildren = function getInvertedChildren(element, id) {
  return toArray(element.querySelectorAll("[" + constants.DATA_INVERSE_FLIP_ID + "=\"" + id + "\"]"));
}; // for stagger


var getFlippedChildrenIds = function getFlippedChildrenIds(element) {
  return toArray(element.querySelectorAll("[" + constants.DATA_FLIP_ID + " ]")).map(function (el) {
    return el.dataset.flipId;
  });
};

export var tweenProp = function tweenProp(start, end, position) {
  return start + (end - start) * position;
};

var animateFlippedElements = function animateFlippedElements(_ref6) {
  var flippedIds = _ref6.flippedIds,
      flipCallbacks = _ref6.flipCallbacks,
      inProgressAnimations = _ref6.inProgressAnimations,
      cachedFlipChildrenPositions = _ref6.cachedFlipChildrenPositions,
      newFlipChildrenPositions = _ref6.newFlipChildrenPositions,
      applyTransformOrigin = _ref6.applyTransformOrigin,
      spring = _ref6.spring,
      getElement = _ref6.getElement,
      debug = _ref6.debug;
  var body = document.querySelector("body");

  if (debug) {
    // eslint-disable-next-line no-console
    console.error('[react-flip-toolkit]\nThe "debug" prop is set to true. All FLIP animations will return at the beginning of the transition.');
  }

  var duplicateFlipIds = getDuplicateValsAsStrings(flippedIds);

  if (duplicateFlipIds.length) {
    // eslint-disable-next-line no-console
    console.error("[react-flip-toolkit]\nThere are currently multiple elements with the same flipId on the page.\nThe animation will only work if each Flipped component has a unique flipId.\nDuplicate flipId" + (duplicateFlipIds.length > 1 ? "s" : "") + ": " + duplicateFlipIds.join("\n"));
  }

  var startFlipFunctions = flippedIds // take all the measurements we need
  // do all the set up work
  // and return a startAnimation function
  .map(function (id) {
    var prevRect = cachedFlipChildrenPositions[id].rect;
    var currentRect = newFlipChildrenPositions[id].rect;
    var prevOpacity = cachedFlipChildrenPositions[id].opacity;
    var currentOpacity = newFlipChildrenPositions[id].opacity;
    var needsForcedMinVals = currentRect.width < 1 || currentRect.height < 1; // don't animate elements outside of the user's viewport

    if (!rectInViewport(prevRect) && !rectInViewport(currentRect)) {
      return;
    } // don't animate elements that didn't change


    if (prevRect.left === currentRect.left && prevRect.top === currentRect.top && prevRect.width === currentRect.width && prevRect.height === currentRect.height && prevOpacity === currentOpacity) {
      return;
    } // it's never going to be visible, so dont animate it


    if (prevRect.width === 0 && currentRect.width === 0 || prevRect.height === 0 && currentRect.height === 0) {
      return;
    }

    var element = getElement(id); // this might happen if we are rapidly adding & removing elements(?)

    if (!element) return;
    var flipConfig = JSON.parse(element.dataset.flipConfig);
    var springConfig = getSpringConfig({
      flipperSpring: spring,
      flippedSpring: flipConfig.spring
    });
    var stagger = flipConfig.stagger === true ? "all" : flipConfig.stagger;
    var childIds = [];

    if (stagger) {
      [].push.apply(childIds, getFlippedChildrenIds(element));
    }

    if (debug) stagger = false;
    var flipStartId = cachedFlipChildrenPositions[id].flipComponentId;
    var flipEndId = flipConfig.componentId;
    if (!shouldApplyTransform(flipConfig.componentIdFilter, flipStartId, flipEndId)) return;
    var currentTransform = Rematrix.parse(newFlipChildrenPositions[id].transform);
    var toVals = {
      matrix: currentTransform
    };
    var fromVals = {};
    var transformsArray = [currentTransform]; // we're only going to animate the values that the child wants animated

    if (flipConfig.translate) {
      transformsArray.push(Rematrix.translateX(prevRect.left - currentRect.left));
      transformsArray.push(Rematrix.translateY(prevRect.top - currentRect.top));
    } // going any smaller than 1px breaks transitions in Chrome


    if (flipConfig.scale) {
      transformsArray.push(Rematrix.scaleX(Math.max(prevRect.width, 1) / Math.max(currentRect.width, 1)));
      transformsArray.push(Rematrix.scaleY(Math.max(prevRect.height, 1) / Math.max(currentRect.height, 1)));
    }

    if (flipConfig.opacity) {
      fromVals.opacity = prevOpacity;
      toVals.opacity = currentOpacity;
    } // we're going to pass around the children in this weird [child, childData]
    // structure because we only want to parse the children's config data 1x


    var invertedChildren = getInvertedChildren(element, id).map(function (c) {
      return [c, JSON.parse(c.dataset.flipConfig)];
    }).filter(function (_ref7) {
      var child = _ref7[0],
          childFlipConfig = _ref7[1];
      return shouldApplyTransform(childFlipConfig.componentIdFilter, flipStartId, flipEndId);
    });
    fromVals.matrix = transformsArray.reduce(Rematrix.multiply);
    fromVals.matrix = convertMatrix3dArrayTo2dArray(fromVals.matrix);
    toVals.matrix = convertMatrix3dArrayTo2dArray(toVals.matrix);
    var applyStyles = createApplyStylesFunc({
      element: element,
      invertedChildren: invertedChildren,
      body: body
    });
    var onComplete = void 0;

    if (flipCallbacks[id] && flipCallbacks[id].onComplete) {
      // must cache or else this could cause an error
      var cachedOnComplete = flipCallbacks[id].onComplete;

      onComplete = function onComplete() {
        return cachedOnComplete(element, flipStartId);
      };
    } // this should be called when animation ends naturally
    // but also when it is interrupted
    // when it is called, the animation has already been cancelled


    var onAnimationEnd = function onAnimationEnd() {
      delete inProgressAnimations[id];

      if (needsForcedMinVals && element) {
        element.style.minHeight = "";
        element.style.minWidth = "";
      }

      isFunction(onComplete) && onComplete();
    };

    var animateOpacity = isNumber(fromVals.opacity) && fromVals.opacity !== toVals.opacity;
    var updateNextFuncParams = void 0;

    var getOnUpdateFunc = function getOnUpdateFunc(nextFunc) {
      return function (stop) {
        return function (_ref8) {
          var currentValue = _ref8.currentValue;

          if (!body.contains(element)) {
            stop();
            return;
          } //stagger


          if (nextFunc && !updateNextFuncParams && currentValue > 0.05) {
            updateNextFuncParams = nextFunc();
          }

          if (updateNextFuncParams) {
            // TODO: make configurable?
            updateNextFuncParams(Math.min(currentValue * 1.5, 1));
          }

          var vals = {};
          vals.matrix = fromVals.matrix.map(function (fromVal, index) {
            return tweenProp(fromVal, toVals.matrix[index], currentValue);
          });

          if (animateOpacity) {
            vals.opacity = tweenProp(fromVals.opacity, toVals.opacity, currentValue);
          }

          applyStyles(vals);
        };
      };
    };

    var startAnimation = function startAnimation(childFuncs) {
      return function (nextFunc) {
        // before animating, immediately apply FLIP styles to prevent flicker
        applyStyles({
          matrix: fromVals.matrix,
          opacity: animateOpacity && fromVals.opacity,
          forceMinVals: needsForcedMinVals
        }); // and batch any other style updates if necessary

        if (flipConfig.transformOrigin) {
          element.style.transformOrigin = flipConfig.transformOrigin;
        } else if (applyTransformOrigin) {
          element.style.transformOrigin = "0 0";
        }

        invertedChildren.forEach(function (_ref9) {
          var child = _ref9[0],
              childFlipConfig = _ref9[1];

          if (childFlipConfig.transformOrigin) {
            child.style.transformOrigin = childFlipConfig.transformOrigin;
          } else if (applyTransformOrigin) {
            child.style.transformOrigin = "0 0";
          }
        }); // return a function that can be called to initialize the actual tweening

        return function () {
          var updateChildFuncParams = [];

          if (childFuncs) {
            // this calls the children functions to start the tweens
            // and returns the updateParams func
            updateChildFuncParams = childFuncs.map(function (f) {
              return f();
            });
          }

          if (debug) {
            var round = function round(n) {
              return Math.round(n * 100) / 100;
            };

            var xAdjust = round(1 / fromVals.matrix[0]);
            var yAdjust = round(1 / fromVals.matrix[3]);
            var boxShadowLeft = xAdjust * 3 + "px 0 0 -" + xAdjust + "px #e2606b";
            var boxShadowRight = "-" + xAdjust * 3 + "px 0 0 -" + xAdjust + "px #e2606b";
            var boxShadowTop = "0 " + yAdjust * 3 + "px 0 -" + yAdjust + "px #e2606b";
            var boxShadowBottom = "0 -" + yAdjust * 3 + "px 0 -" + yAdjust + "px #e2606b";
            var boxShadow = boxShadowLeft + ", " + boxShadowRight + ", " + boxShadowTop + ", " + boxShadowBottom;
            element.style.boxShadow = boxShadow;
            return;
          }

          if (flipCallbacks[id] && flipCallbacks[id].onStart) flipCallbacks[id].onStart(element, flipStartId);
          var spring = springUpdate({
            springConfig: springConfig,
            getOnUpdateFunc: getOnUpdateFunc(nextFunc),
            onAnimationEnd: onAnimationEnd
          }); // in case we have to cancel

          inProgressAnimations[id] = {
            stop: function stop() {
              return spring.stop();
            },
            onComplete: onComplete // for staggering

          };
          return function (toValue) {
            spring.updateConfig({
              toValue: toValue
            });
            updateChildFuncParams.forEach(function (updateFunc) {
              return updateFunc(toValue);
            });
          };
        };
      };
    }; // childIds is only relevant for staggered elements
    // we want to make sure children are staggered too


    return {
      id: id,
      stagger: stagger,
      childIds: childIds,
      startAnimation: startAnimation
    };
  }) // some functions might return undefined
  .filter(function (x) {
    return x;
  });
  var allStaggeredChildrenIds = startFlipFunctions.map(function (data) {
    return data.childIds;
  }).reduce(function (acc, curr) {
    return acc.concat(curr);
  }, []);
  var staggeredChildrenDict = allStaggeredChildrenIds.reduce(function (acc, curr) {
    var _assign;

    return assign(acc, (_assign = {}, _assign[curr] = startFlipFunctions.filter(function (data) {
      return data.id === curr;
    })[0], _assign));
  }, {}); // remove children of staggered flip element

  startFlipFunctions = startFlipFunctions.filter(function (data) {
    return allStaggeredChildrenIds.indexOf(data.id) === -1;
  }); // staggered funcs need to be grouped and provided a reference to the next func

  var staggeredFunctions = startFlipFunctions.filter(function (data) {
    return data.stagger;
  }).reduce(function (acc, curr) {
    var childFuncs = curr.childIds.map(function (id) {
      return (// we don't need to preload with nextFunc or childFuncs because we currently don't allow
        // nested stagger animations
        // check that the function is actually there first
        // because if the element is not in the current window, the object will not exist
        staggeredChildrenDict[id] && staggeredChildrenDict[id].startAnimation()()
      );
    }).filter(function (f) {
      return f;
    });
    var funcPreloadedWithChildrenFunctions = curr.startAnimation(childFuncs);
    if (acc[curr.stagger]) acc[curr.stagger].push(funcPreloadedWithChildrenFunctions);else acc[curr.stagger] = [funcPreloadedWithChildrenFunctions];
    return acc;
  }, {});
  Object.keys(staggeredFunctions).forEach(function (stagger) {
    var funcs = staggeredFunctions[stagger]; // call with reference to the following function

    var startFunc = funcs.reverse().reduce(function (prev, curr) {
      return curr(prev);
    }, function () {});
    startFunc();
  }); // just call unstaggered functions directly

  startFlipFunctions.filter(function (arr) {
    return !arr.stagger;
  }).map(function (arr) {
    return arr.startAnimation;
  }).forEach(function (func) {
    return func()()();
  });
};

export default animateFlippedElements;